import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {UntypedFormControl, Validators} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { finalize, tap } from 'rxjs/operators';
import { DigitalWalletService } from 'app/account/digital-wallet/services/digital-wallet.service';
import { AppState } from '../../../../app.state';
import { NotificationService } from '../../notification/notification.service';
import { NotificationType } from 'app/shared/models/notification-type';
import { TranslateService } from '@ngx-translate/core';
import { ErrorHandlerService } from '../../error-handler/error-handler.service';
import { Subscription } from 'rxjs';
import { UtilityService } from '../../utility/utility.service';
import { IDigitalWalletRewardShip } from 'app/account/digital-wallet/models/digital-wallet-rewards.interface';
import { FileDownloadService } from '../../../../shared/services/file/file-download.service';
import * as _ from 'lodash';     // Imports cloneDeep
import { SpinnerService } from 'app/core/services/spinner/spinner.service';

@Component({
  selector: 'upsc-redeem-confirm-dialog',
  templateUrl: './redeem-confirm-dialog.component.html',
  styleUrls: ['./redeem-confirm-dialog.component.scss'],
})
export class RedeemConfirmDialogComponent implements OnInit, OnDestroy {
    public dialogClass: string;
    public title: string;
    public titleClass: string;
    public message: string;
    public messageClass: string;
    public cancel = 'Cancel';
    public yes = 'Confirm';

    public isSingleInvoice = false;
    public hasMultipleInvoices = false;
    public selectLabel = "Select Invoice"
    public singleInvoiceID;
    public singleInvoiceBalance;

    public customAmount: number;
    public balanceMessage: string | null = null;
    public availablePoints: number | null = null;

    private getOpenInvoiceSubscription: Subscription;
    public openInvoiceLists;
    public selectedOption: string = "option1"; // Auto selects first radio button
    public selectedInvoice: IDigitalWalletRewardShip;
    public oldestInvoice: IDigitalWalletRewardShip;

    public form_customAmount = new UntypedFormControl(null, Validators.compose([Validators.required, Validators.max(this.availablePoints)]));

    constructor(
      public dialogRef: MatDialogRef<RedeemConfirmDialogComponent>,
      @Inject(MAT_DIALOG_DATA)
      public data: any,
      private digitalWalletService: DigitalWalletService,
      private notificationService: NotificationService,
      private translateService: TranslateService,
      private errorHandlerService: ErrorHandlerService,
      private utilityService: UtilityService,
      private spinnerService: SpinnerService,
      private fileDownloadService: FileDownloadService,
      private readonly appState: AppState,
      ) {

      if (this.data) {
        this.dialogClass = this.data.dialogClass ? this.data.dialogClass : '';
        this.title = this.data.title ? this.data.title : '';
        this.titleClass = this.data.titleClass ? this.data.titleClass : '';
        this.message = this.data.message;
        this.messageClass = this.data.messageClass ? this.data.messageClass : '';
        this.cancel = this.data.cancel ? this.data.cancel : this.cancel;
        this.yes = this.data.yes ? this.data.yes : this.yes;

        this.isSingleInvoice = this.data.isSingleInvoice || false;
        this.singleInvoiceID = this.data.singleInvoiceID
        this.singleInvoiceBalance = this.data.singleInvoiceBalance;
      }

      this.setCustomErrors();
    }

    public ngOnInit() {
      this.getDWBalance();
      this.getOpenInvoices();
    }

    public ngOnDestroy() {
      this.utilityService.clearSubscriptions([
        this.getOpenInvoiceSubscription,
      ]);
    }

    private getDWBalance() {
      const currentUser = this.appState.user$();
      this.digitalWalletService.getDWBalance(currentUser?.CustomerId)
        .subscribe(
          (response) => {
            if (response.data !== null) {
              this.availablePoints = response.data.available_points;
              this.form_customAmount.setValidators([Validators.required, Validators.max(this.availablePoints)]);
              this.form_customAmount.updateValueAndValidity();
            }
          },
          (error) => {
            this.notificationService.notify(
              this.errorHandlerService.getHttpErrorMessage(error),
              this.translateService.instant('errorLoadingBalance'),
              NotificationType.ERROR);
          }
      )
    }

    private setCustomErrors(): void {
      this.form_customAmount.valueChanges
      .subscribe(
        (value) => {
          this.customAmount = value;
          if (this.isSingleInvoice) {
            if (Number(this.customAmount) <= this.availablePoints && Number(this.customAmount) > parseFloat(this.singleInvoiceBalance)) {
              this.form_customAmount.setErrors(
                {cannotRedeem: true});
            }
          }
          else if (this.selectedOption === 'option1') {
            if (Number(this.customAmount) <= this.availablePoints && Number(this.customAmount) > parseFloat(this.oldestInvoice?.balance)) {
              this.form_customAmount.setErrors(
                {cannotRedeem: true});
            }
          }
          else if (this.selectedOption === 'option2') {
            if (Number(this.customAmount) <= this.availablePoints && Number(this.customAmount) > parseFloat(this.selectedInvoice?.balance)) {
              this.form_customAmount.setErrors(
                {cannotRedeem: true});
            }
          }
          // TODO: The input type of number turns the value to null if it's NaN.
          //  It will never trigger the error message if the value is null.
          const matches = value === '' || value === null ? [true] : value?.toString()?.match(new RegExp(/^\d*\.?\d{0,2}$/));
          if (!matches?.['0']) {
            this.form_customAmount.setErrors(
              {invalidFormat: true});
          }
        }
      )
    }

    private getOpenInvoices() {
      this.utilityService.clearSubscriptions([this.getOpenInvoiceSubscription]);
      this.getOpenInvoiceSubscription = this.digitalWalletService.getOpenInvoices()
       .subscribe(
         (openInvoices) => {
            if (openInvoices) {
              this.openInvoiceLists = <any[]>_.cloneDeep(openInvoices);

              // DW-117 Option1: Show oldest invoice; Option 2: Show the rest of the invoices(exclude oldest);
              this.oldestInvoice = this.openInvoiceLists[0];
              this.openInvoiceLists.splice(this.oldestInvoice, 1); // Exclude oldest invoice

              if (this.openInvoiceLists.length == 0){
                // When customer has only one invoice no need to display the dropdown
                this.hasMultipleInvoices = false;
                this.selectLabel = "No Invoice found";
              }
              else {
                this.hasMultipleInvoices = true;
                this.selectedInvoice = this.openInvoiceLists[0];
                this.selectLabel = "Select Invoice";
              }
            }
         },
         (error) => {
           // TO DO
         });
     }

     public cancelRewards() {
      utag.link({link_name: "cancel"}); // Tag: track customer clicking cancel button in confirmation pop-up
     }

     public redeemRewards() {
      utag.link({link_name: "redeem"}); // Tag: track customer clicking redeem button in confirmation pop-up
      const {pointsToRedeem, invoiceID} = this.getDataToRedeem();
      const currentUser = this.appState.user$();

      const data: any = {
        customerid: currentUser?.CustomerId,
        points_to_redeem: pointsToRedeem,
        invoiceid: invoiceID,
      }

      this.spinnerService.show();
      this.digitalWalletService.postRedeem(data)
      .pipe(
        finalize(() => {
          this.spinnerService.hide();
        })
      )
      .subscribe((response) => {
        this.dialogRef.close(data);
      });
    }

    private getDataToRedeem() {
      let pointsToRedeem, invoiceID;
      let option1Points: number, option2Points: number;

      if (this.isSingleInvoice) {
          invoiceID = this.singleInvoiceID;
      }
      else {
        if (this.selectedOption === 'option1') {
          option1Points = +this.oldestInvoice?.balance;
          invoiceID = this.oldestInvoice?.invoiceID;
        }
        else if (this.selectedOption === 'option2' && this.selectedInvoice) {
          option2Points = +this.selectedInvoice?.balance;
          invoiceID = this.selectedInvoice?.invoiceID;
        }
      }
      pointsToRedeem = this.customAmount || option1Points || option2Points;
      return {pointsToRedeem, invoiceID};
    }

    public validateAndFormatTwoDecimalPlaces(event: Event) {
      let value: string = (event.target as HTMLInputElement).value;
      const regex: RegExp = /^\d*\.?\d{0,2}$/;

      if (value && !value.match(regex)) {
        this.form_customAmount.setErrors({invalidFormat: true});
      }
    }

    public setCustomAmount(amount: number): void {
        // If an amount is specified, use it.
        if (amount !== null) {
            this.form_customAmount.setValue(amount);
            return;
        }
        // Otherwise, find the max amount that can be applied to an invoice.
        // If the available point is enough for the invoice, use the amount specified in the invoice;
        // otherwise, use the available points.

        // In other words, compare the available points with the amount in question,
        // then select the lesser one.
        let maxAmount = 0;
        if (this.isSingleInvoice) {
            maxAmount = Math.min(this.availablePoints, this.singleInvoiceBalance == null ? 0 : parseFloat(this.singleInvoiceBalance));
            this.form_customAmount.setValue(maxAmount);
            return;
        }
        switch (this.selectedOption) {
            case 'option1':
                maxAmount = Math.min(this.availablePoints, this.oldestInvoice == null ? 0 : parseFloat(this.oldestInvoice.balance));
                break;
            case 'option2':
                if (!this.selectedInvoice) {
                    console.error('No invoice selected');
                    return;
                }
                maxAmount = Math.min(this.availablePoints, this.selectedInvoice == null ? 0 : parseFloat(this.selectedInvoice.balance));
                break;
            default:
                console.error(`Invalid option selection - ${ this.selectedOption }`);
                break;
        }
        this.form_customAmount.setValue(maxAmount);
    }

    public exceedAvailablePointsErrorMessage(): string {
        return this.form_customAmount.hasError('max') ? 'Points can’t exceed available points.' : '';
    }

    public isCustomAmountZero(): boolean {
      return this.form_customAmount.value === 0;
    }

    public remainingPointsRedeemedLaterErrorMessage(): string {
      if (this.isSingleInvoice) {
        return 'You need only ' + Number(this.singleInvoiceBalance).toFixed(2) + ' points to pay your balance. Remaining points can be redeemed later.';
      }
      else if (this.selectedOption === 'option1') {
        return 'You need only ' + Number(this.oldestInvoice.balance).toFixed(2) + ' points to pay your balance. Remaining points can be redeemed later.';
      }
      else if (this.selectedOption === 'option2' && this.selectedInvoice) {
        return 'You need only ' + Number(this.selectedInvoice.balance).toFixed(2) + ' points to pay your balance. Remaining points can be redeemed later.';
      }
    }

    public invalidCustomAmountFormat(): string {
      return 'Enter integer with/without 2 decimal places .';
    }

    public get cannotRedeem(): boolean {
      // Reason for this func is to use along this.form_customAmount.valueChanges() to display remainingPointsRedeemedLaterErrorMessage()
      if (this.isSingleInvoice) {
        return Number(this.customAmount) <= this.availablePoints && Number(this.customAmount) > parseFloat(this.singleInvoiceBalance);
      }
      else if (this.selectedOption === 'option1') {
        return Number(this.customAmount) <= this.availablePoints && Number(this.customAmount) > parseFloat(this.oldestInvoice?.balance);
      }
      else if (this.selectedOption === 'option2' && this.selectedInvoice) {
        return Number(this.customAmount) <= this.availablePoints && Number(this.customAmount) > parseFloat(this.selectedInvoice?.balance);
      }
    }

    public downloadInvoice(invoiceId) {
      this.digitalWalletService.getInvoice(invoiceId.trim()).subscribe(
        (invoice) => {
          this.fileDownloadService.openPDF(invoice.InvoiceEncodedByte, 'Invoice_history.pdf');
        },
        (error) => {
          this.notificationService.notify(
            this.errorHandlerService.getHttpErrorMessage(error),
            this.translateService.instant('errorInvoiceDownload'),
            NotificationType.ERROR);
        });
    }
  }
